import React from 'react';
import { createPortal } from 'react-dom';
import { RotateCcw, Play } from 'lucide-react';
import type { ThemeConfig } from '../../types/forms';
import { getButtonStyles } from '../../lib/themeUtils';
import { timeAgo } from '../../lib/timeago';

interface ResumePromptProps {
  themeConfig: ThemeConfig;
  answeredCount: number;
  totalCount: number;
  lastSavedAt?: string | Date | null;
  onResume: () => void;
  onStartFresh: () => void;
}

export const ResumePrompt: React.FC<ResumePromptProps> = ({
  themeConfig,
  answeredCount,
  totalCount,
  lastSavedAt,
  onResume,
  onStartFresh,
}) => {
  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: themeConfig.backgroundColor, fontFamily: `'${themeConfig.fontFamily}', sans-serif` }}
    >
      {/* Background blob */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[100px] mix-blend-screen" />
      </div>

      {/* Card */}
      <div
        className="relative z-10 w-full max-w-md animate-in fade-in zoom-in-95 duration-300"
      >
        <div className="liquid-glass rounded-3xl p-8 md:p-10 text-center">
          {/* Icon */}
          <div
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: `${themeConfig.buttonBgColor}20` }}
          >
            <span className="text-3xl">📝</span>
          </div>

          {/* Title */}
          <h2
            className="text-xl md:text-2xl font-bold mb-2"
            style={{ color: themeConfig.textColor }}
          >
            Você já começou este formulário
          </h2>

          {/* Progress info */}
          <p
            className="text-base mb-1"
            style={{ color: themeConfig.textColor, opacity: 0.6 }}
          >
            Você respondeu {answeredCount} de {totalCount} perguntas
          </p>

          {/* Time ago */}
          {lastSavedAt && (
            <p
              className="text-sm mb-8"
              style={{ color: themeConfig.textColor, opacity: 0.4 }}
            >
              Última vez: {timeAgo(lastSavedAt)}
            </p>
          )}

          {!lastSavedAt && <div className="mb-8" />}

          {/* Actions */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={onResume}
              style={getButtonStyles(themeConfig)}
              className="w-full flex items-center justify-center gap-2 font-medium transition-all duration-200"
            >
              <Play className="w-4 h-4" />
              Continuar de onde parei
            </button>

            <button
              type="button"
              onClick={onStartFresh}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 font-medium text-base transition-all duration-200 min-h-[48px] rounded-xl"
              style={{
                color: themeConfig.textColor,
                opacity: 0.7,
                backgroundColor: 'transparent',
                border: `1px solid ${themeConfig.textColor}30`,
                borderRadius: `${themeConfig.buttonBorderRadius}px`,
              }}
            >
              <RotateCcw className="w-4 h-4" />
              Começar do zero
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};
